import React from 'react';
import { XMarkIcon, EyeIcon } from '@heroicons/react/24/outline';

interface CreateTokenModalProps {
  showModal: boolean;
  onClose: () => void;
  createForm: {
    name: string;
    domains: string;
    expirationDate: string;
    readPermission: boolean;
    writePermission: boolean;
  };
  onFormChange: (field: keyof CreateTokenModalProps['createForm'], value: string | boolean) => void; // Fixed type
  onSubmit: () => void;
  showExpirationDateInput: boolean;
  setShowExpirationDateInput: (show: boolean) => void;
  domainTags: string[];
  setDomainTags: (tags: string[]) => void;
  domainInput: string;
  setDomainInput: (input: string) => void;
  domainError: string;
  setDomainError: (error: string) => void;
}

const CreateTokenModal: React.FC<CreateTokenModalProps> = ({
  showModal,
  onClose,
  createForm,
  onFormChange,
  onSubmit,
  showExpirationDateInput,
  setShowExpirationDateInput,
  domainTags,
  setDomainTags,
  domainInput,
  setDomainInput,
  domainError,
  setDomainError,
}) => {
  if (!showModal) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="w-full max-w-md bg-white rounded-lg shadow-xl">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h3 className="text-xl font-bold text-gray-900">Create New Token</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6 space-y-6">
          {/* Token Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Token Name
            </label>
            <input
              type="text"
              value={createForm.name}
              onChange={(e) => onFormChange('name', e.target.value)}
              placeholder="Enter token name"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Allowed Domains */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Allowed Domains
            </label>
            <div className="flex flex-wrap items-center border border-gray-300 rounded-md p-2 space-x-2">
              <div className="flex flex-wrap items-center gap-2">
                {domainTags.map((tag, idx) => (
                  <div key={idx} className="flex items-center bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-sm">
                    <span>{tag}</span>
                    <button
                      type="button"
                      className="ml-2 text-blue-500 hover:text-blue-700"
                      onClick={() => {
                        setDomainTags(domainTags.filter((_, i) => i !== idx));
                        setDomainError('');
                      }}
                    >
                      &times;
                    </button>
                  </div>
                ))}
              </div>
              <div className="flex items-center flex-1">
                <input
                  type="text"
                  value={domainInput}
                  onChange={e => setDomainInput(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && domainInput.trim() !== '') {
                      e.preventDefault();
                      const domain = domainInput.trim();

                      // Validate domain format (must contain dot and have extension)
                      if (!domain.includes('.') || domain.split('.').length < 2 || domain.endsWith('.')) {
                        setDomainError('Please enter a valid domain name (e.g., example.com)');
                        return;
                      }

                      if (domainTags.length >= 5) {
                        setDomainError('You can only add up to 5 domains.');
                        return;
                      }
                      if (domainTags.includes(domain)) {
                        setDomainError('This domain is already added.');
                        return;
                      }
                      setDomainTags([...domainTags, domain]);
                      setDomainInput('');
                      setDomainError('');
                    }
                  }}
                  placeholder="Enter a domain (e.g., example.com)"
                  className="flex-1 border-none outline-none focus:ring-0"
                />
                {domainInput.trim() && (
                  <button
                    type="button"
                    className="ml-2 p-1 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded-full transition-colors"
                    onClick={() => {
                      if (domainInput.trim() !== '') {
                        const domain = domainInput.trim();

                        // Validate domain format (must contain dot and have extension)
                        if (!domain.includes('.') || domain.split('.').length < 2 || domain.endsWith('.')) {
                          setDomainError('Please enter a valid domain name (e.g., example.com)');
                          return;
                        }

                        if (domainTags.length >= 5) {
                          setDomainError('You can only add up to 5 domains.');
                          return;
                        }
                        if (domainTags.includes(domain)) {
                          setDomainError('This domain is already added.');
                          return;
                        }
                        setDomainTags([...domainTags, domain]);
                        setDomainInput('');
                        setDomainError('');
                      }
                    }}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
            <p className="mt-1 text-xs text-gray-500">Enter domain names (e.g., example.com, mysite.org) and press Enter or click + to add them to your allowed list.</p>
            {domainError && <p className="mt-2 text-sm text-red-500">{domainError}</p>}
          </div>

          {/* Expiration Date */}
          <div>
            <div className='flex items-center gap-1 mb-3'>
              <input
                type="checkbox"
                id="expirationToggle"
                checked={showExpirationDateInput}
                onChange={(e) => setShowExpirationDateInput(e.target.checked)}
                className="w-4 h-4 px-2 text-blue-600 rounded focus:ring-blue-500"
              />
              <label htmlFor="expirationToggle" className="text-sm text-gray-700">
                Set Expiration Date
              </label>
            </div>
            <div className="relative ">
              <input
                type="date"
                disabled={!showExpirationDateInput}
                value={createForm.expirationDate}
                onChange={(e) => onFormChange('expirationDate', e.target.value)}
                className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <p className="mt-1 text-xs text-gray-500">Leave empty for no expiration</p>
          </div>

          {/* Permissions */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Permissions
            </label>
            <div className="grid grid-cols-2 gap-3">
              {/* Read Permission */}
              <label className="flex items-center p-3 border border-gray-300 rounded-md cursor-pointer hover:bg-gray-50">
                <input
                  type="checkbox"
                  checked={createForm.readPermission}
                  onChange={(e) => onFormChange('readPermission', e.target.checked)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <div className="ml-3 flex items-center">
                  <EyeIcon className="w-4 h-4 text-gray-500 mr-2" />
                  <span className="text-sm font-medium text-gray-700">Read</span>
                </div>
              </label>

              {/* Write Permission */}
              <label className="flex items-center p-3 border border-gray-300 rounded-md cursor-pointer hover:bg-gray-50">
                <input
                  type="checkbox"
                  checked={createForm.writePermission}
                  onChange={(e) => onFormChange('writePermission', e.target.checked)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <div className="ml-3 flex items-center">
                  <svg className="w-4 h-4 text-gray-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  <span className="text-sm font-medium text-gray-700">Write</span>
                </div>
              </label>
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="flex justify-end space-x-3 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-6 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
          >
            Cancel
          </button>
          <button
            onClick={onSubmit}
            className="px-6 py-2 text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-purple-600 rounded-md hover:from-blue-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Create Token
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateTokenModal;